import React, { useCallback, useState } from 'react';
import { addDays } from 'date-fns';
import { MetricsDateRange } from '@modules/monitor/resource-triage/components/MetricsTrendPanel';
import { useFeatureFlag } from 'new/hooks/useFeatureFlags';
import { defineds } from 'js/components/elements/datepicker/defaultRanges';
import { shouldHideRecordsForResource, shouldShowFlowDailyMetrics } from 'js/lib/PipelineUtils';
import FlowOverviewDateControls from './FlowOverviewDateControls';
import FlowDailyMetrics from './FlowDailyMetrics';
import styles from './FlowOverviewMetrics.module.scss';

interface FlowOverviewMetricsProps {
  // Flow resource (flow node merged with its origin resource)
  flow: any;
}

function createInitialOverviewDateRange() {
  return {
    start: addDays(defineds.startOfToday, -6),
    end: defineds.endOfToday,
  };
}

const FlowOverviewMetrics = ({ flow }: FlowOverviewMetricsProps) => {
  const showDailyMetrics = useFeatureFlag('UI_ENABLE_FLOW_INSIGHTS_DAILY_METRICS');
  const [dateRange, setDateRange] = useState(createInitialOverviewDateRange);

  const onDatesChanged = useCallback((dates: MetricsDateRange) => {
    setDateRange({ start: new Date(dates.start), end: new Date(dates.end) });
  }, []);

  // Visibility follows the flow type, the same way the source Overview decides
  // whether to show its metrics panel — not whether the selected period happens
  // to contain runs. Gating on runs hid the section (and its date controls) for
  // flows that hadn't run in the last 7 days, with no way to widen the range.
  if (!showDailyMetrics || !shouldShowFlowDailyMetrics(flow)) return null;

  return (
    // Plain global class (not a CSS module) used as a `:has()` hook so ancestor
    // layouts — e.g. DataFlowOverviewTab's `.doc-tab-fill` — can detect this
    // section is present and stop reserving all the space for a sibling.
    <div className="flow-overview-metrics">
      <div className={styles.divider} />
      <FlowDailyMetrics
        originNodeId={flow.origin_node_id}
        start={dateRange.start}
        end={dateRange.end}
        hideRecords={shouldHideRecordsForResource(flow)}
        showUtcChartLabel
        useLocalCalendarDays
        onDatesChanged={onDatesChanged}
        dateControls={
          <FlowOverviewDateControls
            start={dateRange.start}
            end={dateRange.end}
            onDatesChanged={onDatesChanged}
          />
        }
      />
    </div>
  );
};

FlowOverviewMetrics.displayName = 'FlowOverviewMetrics';

export default FlowOverviewMetrics;
